import { useState } from 'react';
import { cn } from '~/util';
import { useFieldContext } from '~/util/hooks/useFormContext';
import { UserSelector } from '../../ItemSelector';
import {
    FieldLabelWithError,
    FieldWithErrorProps,
} from './FieldLabelWithError';
import { useDisableFormContext } from '../DisableContext';
import { BaseUser } from '~/util/types/users';
import { InteractableDataWrapper } from '~/partials/InteractableDataWrapper';
import { UserTags, UserTagsProps } from '~/partials/Tags';

type UserFieldOptions = Partial<
    Omit<UserTagsProps, 'items' | 'editable' | 'onEdit' | 'item'>
>;

export function UsersField({
    forceDisabled,
    tagOptions = {},
    ...labelProps
}: {
    forceDisabled?: boolean;
    tagOptions?: UserFieldOptions;
} & FieldWithErrorProps) {
    const field = useFieldContext<BaseUser[] | null>();
    const [usersSelectorOpen, setUsersSelectorOpen] = useState(false);
    const disabled = useDisableFormContext() || forceDisabled;
    return (
        <FieldLabelWithError<BaseUser[] | null> {...labelProps}>
            <UserTags
                {...tagOptions}
                items={field.state.value ?? []}
                editable={!disabled}
                onEdit={() => setUsersSelectorOpen(true)}
                onRemove={({ id }) =>
                    field.handleChange(
                        field.state.value?.filter((user) => user.id != id) ??
                            null,
                    )
                }
            />
            <UserSelector
                open={usersSelectorOpen}
                onOpenChange={setUsersSelectorOpen}
                initial={field.state.value ?? []}
                onSave={(items) => {
                    field.handleChange(
                        (items instanceof Array ? items : [items]) ?? null,
                    );
                    setUsersSelectorOpen(false);
                }}
                tagOptions={tagOptions}
            />
        </FieldLabelWithError>
    );
}

export function UserField({
    classname,
    forceDisabled,
    placeholder,
    tagOptions = {},
    onlyPlaceholder,
    onlyVerified,
    ...labelProps
}: {
    forceDisabled?: boolean;
    classname?: string;
    placeholder?: string;
    onlyPlaceholder?: boolean;
    onlyVerified?: boolean;
    tagOptions?: UserFieldOptions;
} & FieldWithErrorProps) {
    const field = useFieldContext<BaseUser | null>();
    const [userSelectorOpen, setUserSelectorOpen] = useState(false);
    const disabled = useDisableFormContext() || forceDisabled;
    return (
        <FieldLabelWithError<BaseUser | null> {...labelProps}>
            <InteractableDataWrapper
                id={field.name}
                open={userSelectorOpen}
                required={labelProps.required}
                disabled={disabled}
                className={cn(
                    field.state.value
                        ? 'text-secondary-foreground'
                        : 'text-secondary-light',
                    classname,
                    disabled && 'opacity-50',
                )}
                onClick={() => {
                    if (!disabled) setUserSelectorOpen(true);
                }}
            >
                {field.state.value?.global_name ?? placeholder ?? 'None'}
            </InteractableDataWrapper>
            <UserSelector
                open={userSelectorOpen}
                onOpenChange={setUserSelectorOpen}
                initial={field.state.value ?? undefined}
                single
                onSave={(items) => {
                    field.handleChange(
                        (items instanceof Array
                            ? items.length > 0
                                ? items[0]
                                : null
                            : items) ?? null,
                    );
                    setUserSelectorOpen(false);
                }}
                onlyPlaceholder={onlyPlaceholder}
                onlyVerified={onlyVerified}
                tagOptions={tagOptions}
            />
        </FieldLabelWithError>
    );
}
